import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Alert from '../ui/Alert';
import Button from '../ui/Button';
import Card from '../ui/Card';
import config from '../../config';
import * as stripeService from '../../utils/stripeService';
import './SubscriptionRoute.css';

/**
 * SubscriptionRoute component
 * Protects routes that require an active subscription
 * If user is not authenticated, redirects to login
 * If user is authenticated but doesn't have an active subscription, shows subscription prompt
 */
const SubscriptionRoute = ({ children }) => {
  const { isAuthenticated, user, hasActiveSubscription, loading, subscriptionLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication and subscription
  if (loading || subscriptionLoading) {
    return (
      <div className="subscription-route-loading">
        <Card 
          title="Loading"
          subtitle="Please wait while we check your subscription status"
          footer={null}
        >
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </Card>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // If authenticated but no subscription, show subscription prompt
  if (!hasActiveSubscription) {
    return (
      <div className="subscription-page">
        <div className="container">
          <div className="subscription-header">
            <h1 className="page-title">Subscription Required</h1>
            <p className="page-subtitle">
              This feature requires a subscription to {config.stripe.productName}
            </p>
          </div>

          <div className="subscription-content">
            <Card 
              title=""
              subtitle=""
              footer=""
              className="subscription-card"
            >
              <div className="subscription-card-header">
                <h2>{config.stripe.productName}</h2>
                <div className="subscription-price">
                  ${config.stripe.amount}<span>/month</span>
                </div>
              </div>
              
              <div className="subscription-card-content">
                <p>
                  Upgrade to {config.stripe.productName} for unlimited access to all features.
                  {config.stripe.productDescription}
                </p>
                
                <div className="subscription-features">
                  <h3>Subscription Benefits</h3>
                  <ul>
                    {config.subscription.features.map((feature, index) => (
                      <li key={index}>
                        <span className="feature-check">✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="subscription-card-footer">
                <div>
                  <button 
                    className="button button-primary"
                    onClick={async () => {
                      try {
                        if (!user?.id || !user?.email) {
                          throw new Error('User information is missing');
                        }
                        
                        const checkoutUrl = await stripeService.createCheckoutSession(
                          user.id,
                          user.email
                        );
                        
                        // Redirect to Stripe checkout
                        window.location.href = checkoutUrl;
                      } catch (error) {
                        console.error('Error creating checkout session:', error);
                        alert('Failed to create checkout session. Please try again.');
                      }
                    }}
                  >
                    <span className="button-text">Subscribe Now</span>
                  </button>
                  
                  <button 
                    className="button button-secondary"
                    onClick={() => window.history.back()}
                  >
                    <span className="button-text">Go Back</span>
                  </button>
                </div>
              </div>
              
              <div className="subscription-secure-payment">
                <div className="alert alert-info" role="alert">
                  <div className="alert-content">
                    <div className="alert-message">
                      <div className="alert-title">Secure Payment</div>
                      <div className="alert-text">All payments are processed securely through Stripe.</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // If authenticated and has subscription, render children
  return children;
};

export default SubscriptionRoute;
